/**
 * HOMPHY SECURITY UTILITIES
 * Centralized sanitization and hardening helpers.
 */

/**
 * Basic string sanitization to prevent XSS and malformed data.
 * Trims whitespace and removes potentially dangerous HTML characters.
 */
export const sanitizeString = (str: string): string => {
  if (!str) return '';
  return str
    .trim()
    .replace(/[<>]/g, '') // Basic tag removal
    .slice(0, 10000); // Prevent extremely long payload attacks
};

/**
 * Sanitizes an object by applying sanitization to all string properties.
 */
export const sanitizeObject = <T extends Record<string, any>>(obj: T): T => {
  if (obj === null || typeof obj !== 'object') return obj;

  // Handle Arrays
  if (Array.isArray(obj)) {
    return obj.map(item => (typeof item === 'object' ? sanitizeObject(item) : item)) as any;
  }

  // Only sanitize plain objects. Skip class instances (GeoPoint, Timestamp, File, Blob, etc.)
  // We use prototype check to be more robust. Plain objects have Object.prototype or null.
  const proto = Object.getPrototypeOf(obj);
  const isPlain = proto === null || proto === Object.prototype;
  
  if (!isPlain || Object.prototype.toString.call(obj) !== '[object Object]') {
    return obj;
  }

  const result: any = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const val = obj[key];
      if (typeof val === 'string') {
        result[key] = sanitizeString(val);
      } else if (val !== null && typeof val === 'object') {
        result[key] = sanitizeObject(val);
      } else {
        result[key] = val;
      }
    }
  }
  return result;
};
